import * as React from 'react';
import { useAppDispatch } from '../../hooks';
import { CircularProgress, Icon, IconButton, Stack, Tooltip } from '@mui/material';
import { styled, darken, lighten } from '@mui/material/styles';
import SearchSkuForMovement from '../../components/Autocomplete/SearchSkuForMovements';
import axios from '../../services/axios';
import { AxiosError, AxiosResponse } from 'axios';
import { getColumns, InventoryMovementProps } from './utils';
import { alertError } from '../../slices/global/alertSlice';
import {
  DataGrid,
  GridCallbackDetails,
  GridCellParams,
  GridToolbar,
  MuiEvent,
} from '@mui/x-data-grid';

export default function InventoryMovementView() {
  const dispatch = useAppDispatch();
  const [selectedSku, setSelectedSku] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [needsRefresh, setNeedsRefresh] = React.useState<boolean>(true);
  const [movements, setMovements] = React.useState<InventoryMovementProps[]>([]);

  React.useEffect(() => {
    const controller = new AbortController();

    async function getData() {
      setLoading(true);
      await axios
        .get('https://wmsapi.autoshack.com/Inventory/GetMovements', {
          params: { sku: selectedSku },
          signal: controller.signal,
        })
        .then((response: AxiosResponse<InventoryMovementProps[] | null>) => {
          if (!response.data) {
            setMovements([]);
          } else {
            console.log(response.data);
            setMovements(response.data);
          }
        })
        .catch((err: AxiosError<InventoryMovementProps[] | null>) => {
          if (err.message !== 'canceled')
            dispatch(
              alertError(
                `Failed to get movements lines for SKU: ${selectedSku}. Please try again.`,
              ),
            );
        })
        .finally(() => {
          setLoading(false);
          setNeedsRefresh(false);
        });
    }

    if (needsRefresh) {
      getData();
    }

    return () => controller.abort();
  }, [dispatch, selectedSku, needsRefresh]);

  const getBackgroundColor = (color: string, mode: string) =>
    mode === 'dark' ? darken(color, 0.7) : lighten(color, 0.7);

  const getHoverBackgroundColor = (color: string, mode: string) =>
    mode === 'dark' ? darken(color, 0.6) : lighten(color, 0.6);

  const getSelectedBackgroundColor = (color: string, mode: string) =>
    mode === 'dark' ? darken(color, 0.5) : lighten(color, 0.5);

  const getSelectedHoverBackgroundColor = (color: string, mode: string) =>
    mode === 'dark' ? darken(color, 0.4) : lighten(color, 0.4);

  const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    '& .super-app-theme--Pending': {
      backgroundColor: getBackgroundColor(theme.palette.info.main, theme.palette.mode),
      '&:hover': {
        backgroundColor: getHoverBackgroundColor(theme.palette.info.main, theme.palette.mode),
      },
      '&.Mui-selected': {
        backgroundColor: getSelectedBackgroundColor(theme.palette.info.main, theme.palette.mode),
        '&:hover': {
          backgroundColor: getSelectedHoverBackgroundColor(
            theme.palette.info.main,
            theme.palette.mode,
          ),
        },
      },
    },
    '& .super-app-theme--Success': {
      backgroundColor: getBackgroundColor(theme.palette.success.main, theme.palette.mode),
      '&:hover': {
        backgroundColor: getHoverBackgroundColor(theme.palette.success.main, theme.palette.mode),
      },
      '&.Mui-selected': {
        backgroundColor: getSelectedBackgroundColor(theme.palette.success.main, theme.palette.mode),
        '&:hover': {
          backgroundColor: getSelectedHoverBackgroundColor(
            theme.palette.success.main,
            theme.palette.mode,
          ),
        },
      },
    },
    '& .super-app-theme--Ignored': {
      backgroundColor: getBackgroundColor(theme.palette.warning.main, theme.palette.mode),
      '&:hover': {
        backgroundColor: getHoverBackgroundColor(theme.palette.warning.main, theme.palette.mode),
      },
      '&.Mui-selected': {
        backgroundColor: getSelectedBackgroundColor(theme.palette.warning.main, theme.palette.mode),
        '&:hover': {
          backgroundColor: getSelectedHoverBackgroundColor(
            theme.palette.warning.main,
            theme.palette.mode,
          ),
        },
      },
    },
    '& .super-app-theme--Failed': {
      backgroundColor: getBackgroundColor(theme.palette.error.main, theme.palette.mode),
      '&:hover': {
        backgroundColor: getHoverBackgroundColor(theme.palette.error.main, theme.palette.mode),
      },
      '&.Mui-selected': {
        backgroundColor: getSelectedBackgroundColor(theme.palette.error.main, theme.palette.mode),
        '&:hover': {
          backgroundColor: getSelectedHoverBackgroundColor(
            theme.palette.error.main,
            theme.palette.mode,
          ),
        },
      },
    },
  }));

  const handleOnChange = (_: any, newValue: string | null) => {
    setSelectedSku(newValue);
    setNeedsRefresh(true);
  };
  const handleOnCellClick = (params: GridCellParams, _: MuiEvent, __: GridCallbackDetails) => {
    if (params.field === 'sku') {
      setSelectedSku(typeof params.value === 'string' ? params.value : null);
      setNeedsRefresh(true);
    }
  };

  const setStatus = async (id: number, status: number) => {
    setLoading(true);

    await axios
      .post(`https://wmsapi.autoshack.com/Inventory/ChangeMovementStatus?id=${id}&status=${status}`)
      .then((response: AxiosResponse<boolean>) => {
        if (!response.data) {
          dispatch(alertError(`Failed update status: ${selectedSku}. Please try again.`));
        }
      })
      .catch((err: AxiosError<boolean>) => {
        if (err.message !== 'canceled')
          dispatch(alertError(`Failed update status: ${selectedSku}. Please try again.`));
      })
      .finally(() => {
        setLoading(false);
        setNeedsRefresh(true);
      });
  };

  const columns = getColumns(setStatus);

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <SearchSkuForMovement selectedSku={selectedSku} onChange={handleOnChange} />

        {loading ? (
          <CircularProgress disableShrink size={32} />
        ) : (
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => setNeedsRefresh(true)}>
              <Icon>restart_alt</Icon>
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <div style={{ height: 800, width: '100%' }}>
        <StyledDataGrid
          rows={movements}
          columns={columns}
          initialState={{
            columns: {
              columnVisibilityModel: {
                id: false,
                occurred: false,
                executedBy: false,
              },
            },
          }}
          getRowId={(x) => x.id}
          loading={loading}
          getRowHeight={() => {
            return 'auto';
          }}
          slots={{
            toolbar: GridToolbar,
          }}
          getRowClassName={(params) => `super-app-theme--${params.row.status}`}
          // slotProps={{
          //   toolbar: {
          //     printOptions: { disableToolbarButton: true },
          //     csvOptions: { disableToolbarButton: true },
          //   },
          // }}
          onCellClick={handleOnCellClick}
          columnBuffer={2}
          columnThreshold={2}
          sx={{
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
          }}
        />
      </div>
    </Stack>
  );
}
